import { useEffect, useRef } from 'react';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import { DEFAULT_COORDS, type Coords } from '@/lib/geo';
import styles from './MapPicker.module.css';

// Leaflet's default marker icons are resolved relative to the CSS file, which
// breaks under a bundler — draw our own marker instead.
const markerIcon = L.divIcon({
  className: styles.markerWrap,
  html: `<span class="${styles.marker}"></span>`,
  iconSize: [22, 22],
  iconAnchor: [11, 11],
});

type Props = {
  value: Coords | null;
  onChange?: (coords: Coords) => void;
  height?: number;
  /** Read-only preview: no click handling, no interaction. */
  preview?: boolean;
};

export default function MapPicker({ value, onChange, height = 320, preview }: Props) {
  const containerRef = useRef<HTMLDivElement>(null);
  const mapRef = useRef<L.Map | null>(null);
  const markerRef = useRef<L.Marker | null>(null);
  const onChangeRef = useRef(onChange);
  onChangeRef.current = onChange;

  useEffect(() => {
    if (!containerRef.current || mapRef.current) return;

    const start = value ?? DEFAULT_COORDS;
    const map = L.map(containerRef.current, {
      center: [start.latitude, start.longitude],
      zoom: value ? 15 : 12,
      zoomControl: !preview,
      dragging: !preview,
      scrollWheelZoom: false,
      doubleClickZoom: !preview,
      touchZoom: !preview,
      keyboard: !preview,
      attributionControl: true,
    });

    L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
      attribution: '&copy; OpenStreetMap',
      maxZoom: 19,
    }).addTo(map);

    if (value) {
      markerRef.current = L.marker([value.latitude, value.longitude], {
        icon: markerIcon,
      }).addTo(map);
    }

    if (!preview) {
      map.on('click', (event: L.LeafletMouseEvent) => {
        const coords = { latitude: event.latlng.lat, longitude: event.latlng.lng };
        onChangeRef.current?.(coords);
      });
    }

    mapRef.current = map;

    // The container is often revealed inside a modal, so its size is only
    // known after the first paint.
    const raf = window.requestAnimationFrame(() => map.invalidateSize());

    return () => {
      window.cancelAnimationFrame(raf);
      map.remove();
      mapRef.current = null;
      markerRef.current = null;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [preview]);

  // Keep the marker and viewport in sync with the selected point.
  useEffect(() => {
    const map = mapRef.current;
    if (!map || !value) return;
    const position: L.LatLngExpression = [value.latitude, value.longitude];
    if (markerRef.current) {
      markerRef.current.setLatLng(position);
    } else {
      markerRef.current = L.marker(position, { icon: markerIcon }).addTo(map);
    }
    map.setView(position, Math.max(map.getZoom(), 15));
  }, [value]);

  return (
    <div className={styles.frame} style={{ height }}>
      <div ref={containerRef} className={styles.map} />
      {!preview ? (
        <p className={styles.hint}>Cliquez sur la carte pour choisir l&apos;emplacement du service.</p>
      ) : null}
    </div>
  );
}
